import React from 'react';
import { createRoot } from 'react-dom/client';
import { initSentry, installUnauthorizedInterceptor } from '@cyberint/common-apps';
import { logBuildInfo, polyglotRegistry } from '@cyberint/common-utils';
import { PointThemeRoot, PointUtils, Themes } from '@cyberint/dsc';
import { adaptBasePath } from './lib/adapt-base-path';

const SHELL_BASE = process.env.SHELL_BASE_PATH_ENV.replace(/\/$/, '');
const { pathname, search, hash } = window.location;
const basePathname = adaptBasePath(pathname, SHELL_BASE);

if (basePathname !== pathname) {
  window.location.replace(`${basePathname}${search}${hash}`);
} else {
  initSentry();
  installUnauthorizedInterceptor();

  if (PointUtils.isPointDS()) {
    document.body.classList.add(Themes.POINT);
  }

  const loadCss = fileName => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('type', 'text/css');
    link.setAttribute(
      'href',
      `${process.env.UI_OLD_PUBLIC_PATH}css/${fileName}?version=${process.env.BUILD_TIMESTAMP}`
    );
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  loadCss('cyberint.css');

  logBuildInfo('SHELL');

  (async () => {
    // Wait for the persisted-locale chunks to load and apply before importing app code —
    // App / its routes freeze t() values at module-load, so importing it before the locale
    // resolves would leave the whole shell rendering English until a second reload. Never
    // block mount on failure though: a thrown chunk-load error would otherwise leave a blank
    // screen; missing translations just fall back to humanized keys via snakeToHumanCase.
    try {
      const s = Date.now();
      await polyglotRegistry.ready();
      // eslint-disable-next-line no-console
      console.log('Polyglot load time (ms)', Date.now() - s);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[i18n-bootstrap] locale load failed; mounting shell with English fallback', err);
    }

    const { default: App } = await import('./App');

    // Libs evaluated inside App's import graph register (Polyglot + i18next) after the first
    // drain — wait for their persisted-locale chunks too so the first render isn't English.
    try {
      const s = Date.now();
      await polyglotRegistry.ready();
      // eslint-disable-next-line no-console
      console.log('Polyglot (2nd ready) load time (ms)', Date.now() - s);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[i18n-bootstrap] late locale load failed; mounting shell with English fallback', err);
    }

    const container = document.getElementById('app');
    const root = createRoot(container);

    // Above <App />, so the tokens are on <body> even if App throws and the error boundary
    // takes over. Mounted once for the whole shell.
    root.render(
      <PointThemeRoot>
        <App />
      </PointThemeRoot>
    );

    document.getElementById('ui-shell-loading-panel')?.remove();
  })();
}
